import { useState, useEffect, useCallback } from 'react';
import { 
  DateFormatConfig, 
  DateFormats, 
  getDateFormats, 
  setDateFormatConfig, 
  saveDateFormatConfig,
  initializeDateFormats,
  getCurrentDateFormatConfig
} from '../config/dateFormats';

/**
 * Hook for managing date format configuration
 * Provides access to current formats and allows changing them
 * Now supports async database operations
 */
export const useDateFormats = () => {
  const [currentFormats, setCurrentFormats] = useState<DateFormats>(getDateFormats());
  const [currentConfig, setCurrentConfig] = useState<DateFormatConfig>('default');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);

  // Update formats when configuration changes
  useEffect(() => {
    setCurrentFormats(getDateFormats());
  }, [currentConfig]);

  // Initialize configuration on mount
  useEffect(() => {
    const initializeConfig = async () => {
      setIsLoading(true);
      setError(null);
      try {
  await initializeDateFormats(selectedCompanyId, true);
  setCurrentFormats(getDateFormats());
  setCurrentConfig(getCurrentDateFormatConfig());
      } catch (err) {
        setError('Failed to load date format configuration');
        console.error('Failed to initialize date formats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeConfig();
  }, [selectedCompanyId]);

  const updateConfig = useCallback(async (config: DateFormatConfig, customFormats?: DateFormats) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const success = await saveDateFormatConfig(config, customFormats, selectedCompanyId);
      if (success) {
        setCurrentConfig(config);
        setCurrentFormats(getDateFormats());
      } else {
        setError('Failed to save date format configuration');
      }
      return success;
    } catch (err) {
      setError('Error saving date format configuration');
      console.error('Failed to update config:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId]);

  const resetToDefault = useCallback(async () => {
    return await updateConfig('default');
  }, [updateConfig]);

  const setToUS = useCallback(async () => {
    return await updateConfig('us');
  }, [updateConfig]);

  const setToISO = useCallback(async () => {
    return await updateConfig('iso');
  }, [updateConfig]);

  const setCustom = useCallback(async (customFormats: DateFormats) => {
    return await updateConfig('custom', customFormats);
  }, [updateConfig]);

  return {
    // Current state
    formats: currentFormats,
    config: currentConfig,
    isLoading,
    error,
  companyId: selectedCompanyId,
    
    // Format getters (convenience methods)
    getDateFormat: (type: 'display' | 'input' | 'api' | 'short' | 'long' = 'display') => 
      currentFormats.date[type],
    getTimeFormat: (type: 'display' | 'input' | 'short' | 'long' | 'ampm' = 'display') => 
      currentFormats.time[type],
    getDateTimeFormat: (type: 'display' | 'input' | 'api' | 'short' | 'long' = 'display') => 
      currentFormats.datetime[type],
    
    // Configuration methods (now async)
    updateConfig,
    resetToDefault,
    setToUS,
    setToISO,
    setCustom,
  setCompanyId: setSelectedCompanyId,
    
    // Utility methods
    clearError: () => setError(null),
  };
};
